import {
  getSelectionListContext,
  Icon,
  IconNames,
  SelectionList,
  SelectionListItemProps,
  Text,
  TextWithHighlight,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { PropsWithChildren, PropsWithClassName } from 'lib-shared-types-utils';
import {
  ComponentProps,
  ElementRef,
  forwardRef,
  KeyboardEvent,
  memo,
  MouseEvent,
  Ref,
  useCallback,
  useContext,
} from 'react';

import styles from './conditionFieldLayoutItem.module.scss';

type ConditionFieldLayoutItemProps<TValue> = {
  value: TValue;
  label: string;
  icon?: IconNames;
  highlight?: string;
  textProps?: Partial<ComponentProps<typeof Text.Paragraph>>;
} & SelectionListItemProps<TValue> &
  PropsWithClassName &
  PropsWithChildren;

const ConditionFieldLayoutItem = <TValue,>(
  {
    className,
    contentClassName,
    children,
    value,
    label,
    icon,
    highlight,
    textProps,
    ...selectionListItemProps
  }: ConditionFieldLayoutItemProps<TValue>,
  ref: Ref<ElementRef<typeof SelectionList.Item>>,
): JSX.Element => {
  const {
    list: { isDisabled: isListDisabled },
    item: { onSelect },
  } = useContext(getSelectionListContext<TValue>());

  /**
   * Custom click event handler for the item.
   *
   * This handler is necessary because each item is wrapped in a `Tooltip`,
   * which renders the item inside a Radix `Trigger` component.
   *
   * The `Trigger` component attaches its own click event handler, which
   * prevents the default click event of the item from being triggered. By
   * providing a custom `onClick` prop, we ensure that Radix calls this function
   * instead of its own handler.
   */
  const handleClick = useCallback(
    (event?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>): void => {
      if (!selectionListItemProps.isDisabled && !isListDisabled) {
        onSelect({
          value,
          meta: {
            target: event?.target,
            event: event ? event : undefined,
          },
        });
      }
    },
    [isListDisabled, onSelect, selectionListItemProps.isDisabled, value],
  );

  return (
    <SelectionList.Item<TValue>
      ref={ref}
      className={classNames(styles.root, className)}
      contentClassName={classNames(styles.content, contentClassName)}
      {...selectionListItemProps}
      value={value}
      onClick={handleClick}
    >
      {icon && <Icon name={icon} color="secondary" />}

      <Text.Paragraph {...textProps}>
        <TextWithHighlight text={label} highlight={highlight} />
      </Text.Paragraph>

      {children}
    </SelectionList.Item>
  );
};

const ConditionFieldLayoutItemMemo = memo(
  forwardRef(ConditionFieldLayoutItem),
) as typeof ConditionFieldLayoutItem;

export { ConditionFieldLayoutItemMemo as ConditionFieldLayoutItem };
